import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Stores the current list of IDs in sessionStorage so detail pages
 * can offer prev/next navigation without going back to the list.
 *
 * On the LIST page:   call `useListNavigationStore('builds', ids)`
 * On the DETAIL page: call `useListNavigation('builds', '/builds')`
 *
 * The detail page variant also accepts an array of [listKey, basePath] pairs
 * and will use whichever context contains the current item ID — useful when
 * a detail page can be reached from multiple list pages.
 */

const KEY_PREFIX = 'list-nav-';

/** Call from the list page to persist the current filtered/sorted IDs. */
export function useListNavigationStore(listKey: string, ids: string[]) {
  useEffect(() => {
    sessionStorage.setItem(`${KEY_PREFIX}${listKey}`, JSON.stringify(ids));
  }, [listKey, ids]);
}

/** Call from the detail page to get prev/next helpers.
 *  `listKey` can be a single key string or an array of keys to try in order.
 *  The first key whose stored list contains the current ID wins.
 */
export function useListNavigation(
  listKey: string | string[],
  basePath: string,
) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { ids, activeBasePath } = useMemo(() => {
    const keys = Array.isArray(listKey) ? listKey : [listKey];
    for (const key of keys) {
      try {
        const stored: string[] = JSON.parse(
          sessionStorage.getItem(`${KEY_PREFIX}${key}`) || '[]',
        );
        if (id && stored.includes(id)) {
          return { ids: stored, activeBasePath: basePath };
        }
      } catch {
        // ignore parse errors
      }
    }
    return { ids: [] as string[], activeBasePath: basePath };
  }, [listKey, basePath, id]);

  const currentIndex = id ? ids.indexOf(id) : -1;
  const hasList = ids.length > 0 && currentIndex >= 0;
  const hasPrev = hasList && currentIndex > 0;
  const hasNext = hasList && currentIndex < ids.length - 1;
  const position = hasList ? `${currentIndex + 1} / ${ids.length}` : null;

  const goPrev = () => {
    if (hasPrev) navigate(`${activeBasePath}/${ids[currentIndex - 1]}`);
  };
  const goNext = () => {
    if (hasNext) navigate(`${activeBasePath}/${ids[currentIndex + 1]}`);
  };

  return { hasPrev, hasNext, goPrev, goNext, position };
}
